import { appLanguage } from "../i18n/strings";
import { VIDEO_SERVER_BASE_URL } from "../config/videoServer";
import { refreshPlaybackCachePolicy, suspendPlaybackCache } from "./mobileHlsCache";
import type { ColumnVideo } from "../screens/columns";
import type { ProductPlacement, ShopOrder, ShopProduct } from "../../types";

const VIDEO_CACHE_KEY = "kirkwat-video-library-cache-v3";

export type DesktopVideoSort =
  | "personalized"
  | "favorites"
  | "public"
  | "manualScore"
  | "clicks"
  | "watchTime"
  | "comments"
  | "latest";

export type SmartPlaybackPolicy = {
  relatedTermsWeight: number;
  sameChannelWeight: number;
  sameCategoryWeight: number;
  personalizedWeight: number;
  publicWeight: number;
  advertisementRelevanceWeight: number;
  explorationPercent: number;
  recentHistorySize: number;
};

export const DEFAULT_SMART_PLAYBACK_POLICY: SmartPlaybackPolicy = {
  relatedTermsWeight: 30,
  sameChannelWeight: 60,
  sameCategoryWeight: 20,
  personalizedWeight: 35,
  publicWeight: 10,
  advertisementRelevanceWeight: 20,
  explorationPercent: 8,
  recentHistorySize: 12,
};

export type FetchVideoOptions = {
  surface?: "desktop" | "mobile";
  sort?: DesktopVideoSort;
  visitorId?: string;
  userId?: string;
  limit?: number;
  offset?: number;
};

export type ColumnVideoPage = {
  videos: ColumnVideo[];
  total: number;
  limit: number;
  offset: number;
};

const categoryKeys = new Set([
  "freshVideos",
  "games",
  "music",
  "shorts",
  "live",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function text(value: unknown, fallback = "") {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function publicCodecLabel(value: unknown) {
  return text(value)
    .replace(/\s*[（(]Intel\s+Arc\s+A380\s+优先[）)]/gi, "")
    .replace(/Intel\s+Arc\s+A380/gi, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function normalizeMediaVariants(
  value: unknown,
): NonNullable<ColumnVideo["mediaVariants"]> {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const kind = text(item.kind);
    const url = text(item.url);
    if (!url || !["mobile", "desktop", "audio"].includes(kind)) return [];
    return [
      {
        id: text(item.id, url),
        kind: kind as "mobile" | "desktop" | "audio",
        label: publicCodecLabel(item.label),
        format: text(item.format),
        quality: text(item.quality),
        resolution: text(item.resolution),
        bitrate: text(item.bitrate),
        codec: text(item.codec),
        isDefault: item.isDefault === true,
        url,
      },
    ];
  });
}

function numberValue(value: unknown, fallback = 0) {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function normalizeSmartPlaybackPolicy(value: unknown): SmartPlaybackPolicy {
  const source = isRecord(value) ? value : {};
  const bounded = (key: keyof SmartPlaybackPolicy, maximum = 100) =>
    Math.max(
      0,
      Math.min(maximum, numberValue(source[key], DEFAULT_SMART_PLAYBACK_POLICY[key])),
    );
  return {
    relatedTermsWeight: bounded("relatedTermsWeight"),
    sameChannelWeight: bounded("sameChannelWeight"),
    sameCategoryWeight: bounded("sameCategoryWeight"),
    personalizedWeight: bounded("personalizedWeight"),
    publicWeight: bounded("publicWeight"),
    advertisementRelevanceWeight: bounded("advertisementRelevanceWeight"),
    explorationPercent: bounded("explorationPercent"),
    recentHistorySize: Math.round(bounded("recentHistorySize", 50)),
  };
}

function normalizeShopProduct(value: unknown): ShopProduct | null {
  if (!isRecord(value)) return null;
  const productId = text(value.productId);
  const name = text(value.name);
  const pointPrice = Math.max(0, Math.floor(numberValue(value.pointPrice)));
  if (!productId || !name || pointPrice < 1) return null;
  return {
    productId,
    name,
    subtitle: text(value.subtitle),
    description: text(value.description),
    imageUrl: text(value.imageUrl),
    detailImages: Array.isArray(value.detailImages)
      ? value.detailImages.filter(
          (item): item is string =>
            typeof item === "string" && Boolean(item.trim()),
        )
      : [],
    badge: text(value.badge),
    pointPrice,
    originalPointPrice: Math.max(
      pointPrice,
      Math.floor(numberValue(value.originalPointPrice, pointPrice)),
    ),
    stock: Math.max(0, Math.floor(numberValue(value.stock))),
    videoIds: Array.isArray(value.videoIds)
      ? value.videoIds.filter(
          (item): item is string => typeof item === "string",
        )
      : undefined,
  };
}

function normalizeProductPlacements(value: unknown): ProductPlacement[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const product = normalizeShopProduct(item);
    if (!product || !isRecord(item)) return [];
    return [
      {
        ...product,
        placementId: text(item.placementId, product.productId),
        startSeconds: Math.max(0, numberValue(item.startSeconds)),
        endSeconds: Math.max(0, numberValue(item.endSeconds)),
        enabled: item.enabled !== false,
      },
    ];
  });
}

function normalizeVideo(value: unknown): ColumnVideo | null {
  if (!isRecord(value)) {
    return null;
  }

  const videoUrl = text(value.videoUrl);
  if (!videoUrl) {
    return null;
  }

  const category = text(value.category);
  const normalizedCategory = categoryKeys.has(category)
    ? category
    : "freshVideos";

  const rankingValue = isRecord(value.ranking) ? value.ranking : null;
  const metricsValue =
    rankingValue && isRecord(rankingValue.metrics) ? rankingValue.metrics : {};
  const userSignalsValue =
    rankingValue && isRecord(rankingValue.userSignals)
      ? rankingValue.userSignals
      : {};
  return {
    id: text(value.id, videoUrl),
    title: text(value.title, "Imported video"),
    channel: text(value.channel, "Local Channel"),
    channelId: text(value.channelId),
    meta: text(value.meta, "Imported locally"),
    thumbnail: text(value.thumbnail),
    portraitThumbnail: text(value.portraitThumbnail),
    subtitleUrl: text(value.subtitleUrl),
    videoUrl,
    mobileVideoUrl: text(value.mobileVideoUrl, videoUrl),
    desktopVideoUrl: text(value.desktopVideoUrl, videoUrl),
    backgroundMediaUrl: text(value.backgroundMediaUrl, videoUrl),
    mediaVariants: normalizeMediaVariants(value.mediaVariants),
    productPlacements: normalizeProductPlacements(value.productPlacements),
    productEditorAllowed: value.productEditorAllowed === true,
    p2pEnabled: value.p2pEnabled === true,
    p2pConfigured: value.p2pConfigured === true,
    p2pViewerCount:
      typeof value.p2pViewerCount === "number" ? value.p2pViewerCount : 0,
    tags: Array.isArray(value.tags)
      ? value.tags.filter((item): item is string => typeof item === "string")
      : [],
    rankingScore:
      typeof value.rankingScore === "number" ? value.rankingScore : 50,
    isAdvertisement: value.isAdvertisement === true,
    adDisplayMode:
      value.adDisplayMode === "keyword" || value.adDisplayMode === "broad"
        ? value.adDisplayMode
        : "smart",
    adMaxPlays:
      typeof value.adMaxPlays === "number"
        ? Math.max(1, Math.floor(value.adMaxPlays))
        : 1000,
    adPlayCount:
      typeof value.adPlayCount === "number"
        ? Math.max(0, Math.floor(value.adPlayCount))
        : 0,
    adRemainingPlays:
      typeof value.adRemainingPlays === "number"
        ? Math.max(0, Math.floor(value.adRemainingPlays))
        : 0,
    adKeywords: Array.isArray(value.adKeywords)
      ? value.adKeywords.filter(
          (item): item is string => typeof item === "string",
        )
      : [],
    isPaid: value.isPaid === true,
    coinPrice:
      value.isPaid === true && typeof value.coinPrice === "number"
        ? Math.max(1, Math.floor(value.coinPrice))
        : 0,
    vipOnly: value.vipOnly === true,
    vipLocked: value.vipLocked === true,
    ranking: rankingValue
      ? {
          mode: text(rankingValue.mode, "public"),
          position:
            typeof rankingValue.position === "number"
              ? rankingValue.position
              : 0,
          publicScore:
            typeof rankingValue.publicScore === "number"
              ? rankingValue.publicScore
              : 0,
          personalizedScore:
            typeof rankingValue.personalizedScore === "number"
              ? rankingValue.personalizedScore
              : 0,
          personalizationConfidence:
            typeof rankingValue.personalizationConfidence === "number"
              ? rankingValue.personalizationConfidence
              : 0,
          userSignals: {
            clicks:
              typeof userSignalsValue.clicks === "number"
                ? userSignalsValue.clicks
                : 0,
            watchSeconds:
              typeof userSignalsValue.watchSeconds === "number"
                ? userSignalsValue.watchSeconds
                : 0,
            favorited: userSignalsValue.favorited === true,
            commented: userSignalsValue.commented === true,
            tagAffinity:
              typeof userSignalsValue.tagAffinity === "number"
                ? userSignalsValue.tagAffinity
                : 0,
          },
          reason: text(rankingValue.reason, "新内容"),
          metrics: {
            clicks:
              typeof metricsValue.clicks === "number" ? metricsValue.clicks : 0,
            watchSeconds:
              typeof metricsValue.watchSeconds === "number"
                ? metricsValue.watchSeconds
                : 0,
            comments:
              typeof metricsValue.comments === "number"
                ? metricsValue.comments
                : 0,
          },
        }
      : undefined,
    duration: text(value.duration, "0:00"),
    category: normalizedCategory as ColumnVideo["category"],
    createdAt: text(value.createdAt),
    updatedAt: text(value.updatedAt),
  };
}

export type PaidAccount = {
  id: string;
  email: string;
  label: string;
  balance: number;
  totalRecharged: number;
  totalSpent: number;
  purchaseCount: number;
  membershipTier: "member" | "vip" | "admin";
  isVip: boolean;
  canInsertProducts: boolean;
  isAdmin: boolean;
};

export type PaidEntitlement = {
  isPaid: boolean;
  coinPrice: number;
  authenticated: boolean;
  entitled: boolean;
  purchased: boolean;
  account: PaidAccount | null;
};

export type AdminMediaVariant = {
  id: string;
  kind: "mobile" | "desktop" | "audio" | "original";
  label: string;
  source: string;
  resolvedUrl?: string;
  storage?: string;
  format?: string;
  quality?: string;
  resolution?: string;
  bitrate?: string;
  codec?: string;
  isDefault?: boolean;
};

export type AdminVideo = {
  id: string;
  title: string;
  channel: string;
  meta?: string;
  duration?: string;
  category?: string;
  tags?: string[];
  manualTags?: string[];
  thumbnail?: string;
  thumbnailUrl?: string;
  orientation?: "portrait" | "landscape" | "square" | "unknown";
  subtitleStatus?: "embedded" | "api-ready" | "api-required";
  subtitleUrl?: string;
  sourceUrl?: string;
  mediaVariants: AdminMediaVariant[];
};

export type AdminVideoOrganization = {
  title: string;
  tags: string[];
  category: string;
  duration: string;
  width: number;
  height: number;
  orientation: "portrait" | "landscape" | "square" | "unknown";
  recommendedKind: "mobile" | "desktop";
  codec: string;
  thumbnailPath: string;
  subtitlePath: string;
  embeddedSubtitleTracks: number;
  subtitleStatus: "embedded" | "api-ready" | "api-required";
};

export class PaidVideoApiError extends Error {
  code: string;
  account: PaidAccount | null;
  coinPrice: number;

  constructor(message: string, payload: Record<string, unknown> = {}) {
    super(message);
    this.name = "PaidVideoApiError";
    this.code = text(payload.code);
    this.account = isRecord(payload.account)
      ? normalizePaidAccount(payload.account)
      : null;
    this.coinPrice =
      typeof payload.coinPrice === "number" ? payload.coinPrice : 0;
  }
}

function normalizePaidAccount(value: Record<string, unknown>): PaidAccount {
  return {
    id: text(value.id),
    email: text(value.email),
    label: text(value.label),
    balance: typeof value.balance === "number" ? value.balance : 0,
    totalRecharged:
      typeof value.totalRecharged === "number" ? value.totalRecharged : 0,
    totalSpent: typeof value.totalSpent === "number" ? value.totalSpent : 0,
    purchaseCount:
      typeof value.purchaseCount === "number" ? value.purchaseCount : 0,
    membershipTier:
      value.membershipTier === "admin"
        ? "admin"
        : value.membershipTier === "vip"
          ? "vip"
          : "member",
    isVip:
      value.membershipTier === "admin" ||
      value.membershipTier === "vip" ||
      value.isVip === true,
    canInsertProducts: value.canInsertProducts === true,
    isAdmin: value.isAdmin === true,
  };
}

async function paidApiJson(
  response: Response,
): Promise<Record<string, unknown>> {
  const payload: unknown = await response.json().catch(() => ({}));
  const body = isRecord(payload) ? payload : {};
  if (!response.ok) {
    throw new PaidVideoApiError(
      text(body.error, `付费服务请求失败 (${response.status})`),
      body,
    );
  }
  return body;
}

export async function fetchAdminVideos(): Promise<AdminVideo[]> {
  const response = await fetch(`${VIDEO_SERVER_BASE_URL}/api/admin/videos`, {
    cache: "no-store",
    credentials: "include",
  });
  const payload: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const body = isRecord(payload) ? payload : {};
    throw new PaidVideoApiError(
      text(body.error, `管理员视频请求失败 (${response.status})`),
      body,
    );
  }
  return Array.isArray(payload) ? (payload as AdminVideo[]) : [];
}

export async function updateAdminVideo(
  videoId: string,
  changes: Partial<AdminVideo>,
): Promise<AdminVideo> {
  return (await paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/videos/${encodeURIComponent(videoId)}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(changes),
    }),
  )) as unknown as AdminVideo;
}

export async function createAdminVideo(input: Partial<AdminVideo>) {
  const response = await fetch(`${VIDEO_SERVER_BASE_URL}/api/videos/bulk`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ videos: [input] }),
  });
  const payload: unknown = await response.json().catch(() => ({}));
  if (!response.ok) {
    const body = isRecord(payload) ? payload : {};
    throw new PaidVideoApiError(
      text(body.error, `新增视频失败 (${response.status})`),
      body,
    );
  }
  return Array.isArray(payload) ? payload[0] : payload;
}

export async function organizeAdminVideo(input: {
  source: string;
  title?: string;
  tags?: string[];
  category?: string;
}): Promise<AdminVideoOrganization> {
  return (await paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/admin/video-organize/preview`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, generateThumbnail: true }),
    }),
  )) as unknown as AdminVideoOrganization;
}

export async function deleteAdminVideo(videoId: string, fullChain = false) {
  return paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/videos/${encodeURIComponent(videoId)}${fullChain ? "?scope=full" : ""}`, {
      method: "DELETE",
      credentials: "include",
    }),
  );
}

export async function createPaidAccountSession(
  email: string,
  password: string,
) {
  const payload = await paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/paid/session`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    }),
  );
  suspendPlaybackCache();
  await refreshPlaybackCachePolicy().catch(() => {});
  return {
    authenticated: payload.authenticated === true,
    account: isRecord(payload.account)
      ? normalizePaidAccount(payload.account)
      : null,
  };
}

export async function registerPaidAccount(
  email: string,
  password: string,
  label = "",
) {
  const payload = await paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/paid/register`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, label }),
    }),
  );
  suspendPlaybackCache();
  await refreshPlaybackCachePolicy().catch(() => {});
  return {
    authenticated: payload.authenticated === true,
    account: isRecord(payload.account)
      ? normalizePaidAccount(payload.account)
      : null,
  };
}

export async function fetchPaidAccountSession() {
  const response = await fetch(`${VIDEO_SERVER_BASE_URL}/api/paid/account`, {
    credentials: "include",
    cache: "no-store",
  });
  if (response.status === 401) return null;
  const payload = await paidApiJson(response);
  return isRecord(payload.account)
    ? normalizePaidAccount(payload.account)
    : null;
}

export async function fetchSmartPlaybackPolicy(): Promise<{
  accountId: string | null;
  source: "account" | "default";
  policy: SmartPlaybackPolicy;
}> {
  const payload = await paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/playback/smart-policy`, {
      credentials: "include",
      cache: "no-store",
    }),
  );
  return {
    accountId: text(payload.accountId) || null,
    source: payload.source === "account" ? "account" : "default",
    policy: normalizeSmartPlaybackPolicy(payload.policy),
  };
}

export async function saveSmartPlaybackPolicy(
  policy: SmartPlaybackPolicy,
): Promise<{
  accountId: string;
  source: "account";
  policy: SmartPlaybackPolicy;
}> {
  const payload = await paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/playback/smart-policy`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ policy }),
    }),
  );
  return {
    accountId: text(payload.accountId),
    source: "account",
    policy: normalizeSmartPlaybackPolicy(payload.policy),
  };
}

export async function resetSmartPlaybackPolicy(): Promise<{
  accountId: string;
  source: "default";
  policy: SmartPlaybackPolicy;
}> {
  const payload = await paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/playback/smart-policy`, {
      method: "DELETE",
      credentials: "include",
      cache: "no-store",
    }),
  );
  return {
    accountId: text(payload.accountId),
    source: "default",
    policy: normalizeSmartPlaybackPolicy(payload.policy),
  };
}

export async function clearPaidAccountSession() {
  suspendPlaybackCache();
  await paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/paid/session`, {
      method: "DELETE",
      credentials: "include",
      cache: "no-store",
    }),
  );
  await refreshPlaybackCachePolicy().catch(() => {});
}

export async function fetchPaidVideoEntitlement(
  videoId: string,
): Promise<PaidEntitlement> {
  const payload = await paidApiJson(
    await fetch(
      `${VIDEO_SERVER_BASE_URL}/api/paid/videos/${encodeURIComponent(
        videoId,
      )}/entitlement`,
      { credentials: "include", cache: "no-store" },
    ),
  );
  return {
    isPaid: payload.isPaid === true,
    coinPrice: typeof payload.coinPrice === "number" ? payload.coinPrice : 0,
    authenticated: payload.authenticated === true,
    entitled: payload.entitled === true,
    purchased: payload.purchased === true,
    account: isRecord(payload.account)
      ? normalizePaidAccount(payload.account)
      : null,
  };
}

export async function purchasePaidVideo(videoId: string) {
  const payload = await paidApiJson(
    await fetch(
      `${VIDEO_SERVER_BASE_URL}/api/paid/videos/${encodeURIComponent(
        videoId,
      )}/purchase`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      },
    ),
  );
  return {
    entitled: payload.entitled === true,
    duplicate: payload.duplicate === true,
    account: isRecord(payload.account)
      ? normalizePaidAccount(payload.account)
      : null,
  };
}

export async function fetchShopProduct(
  productId: string,
): Promise<ShopProduct> {
  const payload = await paidApiJson(
    await fetch(
      `${VIDEO_SERVER_BASE_URL}/api/shop/products/${encodeURIComponent(
        productId,
      )}`,
      {
        credentials: "include",
        cache: "no-store",
      },
    ),
  );
  const product = normalizeShopProduct(payload.product);
  if (!product) throw new Error("商品数据无效");
  return product;
}

export async function fetchShopProducts(): Promise<ShopProduct[]> {
  const payload = await paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/shop/products`, {
      credentials: "include",
      cache: "no-store",
    }),
  );
  return Array.isArray(payload.products)
    ? payload.products.flatMap((value) => {
        const product = normalizeShopProduct(value);
        return product ? [product] : [];
      })
    : [];
}

export async function fetchShopOrders(): Promise<{
  account: PaidAccount;
  orders: ShopOrder[];
}> {
  const payload = await paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/shop/orders`, {
      credentials: "include",
      cache: "no-store",
    }),
  );
  const account = isRecord(payload.account)
    ? normalizePaidAccount(payload.account)
    : null;
  if (!account) throw new Error("账号数据无效");
  const orders = Array.isArray(payload.orders)
    ? payload.orders.flatMap((value): ShopOrder[] => {
        if (!isRecord(value)) return [];
        const items = Array.isArray(value.items)
          ? value.items.flatMap((item) => {
              if (!isRecord(item)) return [];
              return [
                {
                  productId: text(item.productId),
                  name: text(item.name),
                  imageUrl: text(item.imageUrl),
                  quantity: Math.max(
                    1,
                    Math.floor(numberValue(item.quantity, 1)),
                  ),
                  unitPrice: Math.max(
                    0,
                    Math.floor(numberValue(item.unitPrice)),
                  ),
                  subtotal: Math.max(0, Math.floor(numberValue(item.subtotal))),
                },
              ];
            })
          : [];
        return [
          {
            id: text(value.id),
            requestId: text(value.requestId),
            items,
            totalPoints: Math.max(
              0,
              Math.floor(numberValue(value.totalPoints)),
            ),
            status: value.status === "cancelled" ? "cancelled" : "paid",
            createdAt: text(value.createdAt),
          },
        ];
      })
    : [];
  return { account, orders };
}

export async function checkoutShop(
  items: { productId: string; quantity: number }[],
  requestId: string,
) {
  const payload = await paidApiJson(
    await fetch(`${VIDEO_SERVER_BASE_URL}/api/shop/checkout`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ requestId, items }),
    }),
  );
  const orderValue = isRecord(payload.order) ? payload.order : {};
  return {
    duplicate: payload.duplicate === true,
    order: {
      id: text(orderValue.id),
      requestId: text(orderValue.requestId),
      items: Array.isArray(orderValue.items) ? orderValue.items : [],
      totalPoints: Math.max(0, Math.floor(numberValue(orderValue.totalPoints))),
      status:
        orderValue.status === "cancelled"
          ? ("cancelled" as const)
          : ("paid" as const),
      createdAt: text(orderValue.createdAt),
    } as ShopOrder,
    account: isRecord(payload.account)
      ? normalizePaidAccount(payload.account)
      : null,
  };
}

export async function fetchVideoProductEditor(videoId: string) {
  const payload = await paidApiJson(
    await fetch(
      `${VIDEO_SERVER_BASE_URL}/api/shop/videos/${encodeURIComponent(
        videoId,
      )}/product-editor`,
      { credentials: "include", cache: "no-store" },
    ),
  );
  return {
    allowed: payload.allowed === true,
    videoId: text(payload.videoId),
    videoTitle: text(payload.videoTitle),
    productPlacements: normalizeProductPlacements(payload.productPlacements),
    products: Array.isArray(payload.products)
      ? payload.products.flatMap((value) => {
          const product = normalizeShopProduct(value);
          return product ? [product] : [];
        })
      : [],
  };
}

export async function saveVideoProductEditor(
  videoId: string,
  productPlacements: ProductPlacement[],
) {
  const payload = await paidApiJson(
    await fetch(
      `${VIDEO_SERVER_BASE_URL}/api/shop/videos/${encodeURIComponent(
        videoId,
      )}/product-editor`,
      {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productPlacements }),
      },
    ),
  );
  return normalizeProductPlacements(payload.productPlacements);
}

export async function fetchP2pStatus(videoId: string) {
  const response = await fetch(
    `${VIDEO_SERVER_BASE_URL}/api/p2p-status/${encodeURIComponent(videoId)}`,
    { cache: "no-store", credentials: "include" },
  );
  if (!response.ok) throw new Error(`P2P status responded ${response.status}`);
  const value: unknown = await response.json();
  if (!isRecord(value)) throw new Error("Invalid P2P status response");
  return {
    configured: value.configured === true,
    active: value.active === true,
    viewerCount: typeof value.viewerCount === "number" ? value.viewerCount : 0,
    viewerThreshold:
      typeof value.viewerThreshold === "number" ? value.viewerThreshold : 1,
    mode: text(value.mode, "off"),
  };
}

export async function fetchColumnVideoPage(
  options: FetchVideoOptions = {},
): Promise<ColumnVideoPage> {
  const params = new URLSearchParams({ lang: appLanguage });
  if (options.surface) params.set("surface", options.surface);
  if (options.sort) params.set("sort", options.sort);
  if (options.visitorId) params.set("visitorId", options.visitorId);
  if (options.userId) params.set("userId", options.userId);
  const limit = Math.max(1, Math.min(Math.floor(options.limit || 60), 200));
  const offset = Math.max(0, Math.floor(options.offset || 0));
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const cacheKey = `${VIDEO_CACHE_KEY}:${options.surface || "mobile"}:${
    options.sort || "default"
  }:${options.userId || options.visitorId || "public"}:${offset}:${limit}`;
  try {
    const response = await fetch(
      `${VIDEO_SERVER_BASE_URL}/api/videos?${params}`,
      { credentials: "include" },
    );
    if (!response.ok) {
      throw new Error(`Video server responded ${response.status}`);
    }
    const payload: unknown = await response.json();
    const videos = Array.isArray(payload)
      ? payload
          .map((item) => normalizeVideo(item))
          .filter((item): item is ColumnVideo => Boolean(item))
      : [];
    const totalHeader = Number(response.headers.get("X-Total-Count"));
    const total = Number.isFinite(totalHeader)
      ? Math.max(offset + videos.length, Math.floor(totalHeader))
      : offset + videos.length;
    try {
      if (
        typeof window !== "undefined" &&
        typeof window.localStorage?.setItem === "function" &&
        videos.length
      ) {
        window.localStorage.setItem(
          cacheKey,
          JSON.stringify({ videos, total, savedAt: Date.now() }),
        );
      }
    } catch (cacheError) {
      // A full or unavailable cache must not discard a successful response.
      console.warn("Video library cache write skipped:", cacheError);
    }
    return { videos, total, limit, offset };
  } catch (error) {
    if (
      typeof window !== "undefined" &&
      typeof window.localStorage?.getItem === "function"
    ) {
      try {
        const cached = JSON.parse(
          window.localStorage.getItem(cacheKey) || "null",
        );
        if (Array.isArray(cached?.videos) && cached.videos.length) {
          console.warn("Using cached video library fallback:", error);
          return {
            videos: cached.videos as ColumnVideo[],
            total: Number.isFinite(Number(cached.total))
              ? Math.max(
                  offset + cached.videos.length,
                  Math.floor(Number(cached.total)),
                )
              : offset + cached.videos.length,
            limit,
            offset,
          };
        }
      } catch {}
    }
    throw error;
  }
}

export async function fetchColumnVideos(options: FetchVideoOptions = {}) {
  const page = await fetchColumnVideoPage(options);
  return page.videos;
}

export async function fetchAllColumnVideos(options: FetchVideoOptions = {}) {
  const pageSize = Math.max(1, Math.min(Math.floor(options.limit || 100), 200));
  const videos: ColumnVideo[] = [];
  for (let offset = 0; offset < 2000; offset += pageSize) {
    const page = await fetchColumnVideos({ ...options, limit: pageSize, offset });
    videos.push(...page);
    if (page.length < pageSize) break;
  }
  return videos;
}
